import { CyclesInterface } from 'types/user';

import { canApproveParticipantsCount } from '../ApreciarRealizacao/normal/rules';
import { emBrasilia, etapasDoCiclo } from '../calendario-do-ciclo';

/**
 * Regras da autorização do número de participantes (spec `minha-equipe-participantes`).
 * Campos de `find/autorizacao_participantes`; datas em milissegundos.
 */

export type StatusDaAutorizacao = 'pending' | 'approved' | 'rejected';

export interface AprovacaoDeParticipantes {
  status?: string;
  decision?: string;
  decided_by?: string;
  decided_team_size?: number;
}

export interface RealizacaoParaAutorizar {
  _id: string;
  title?: string;
  owner?: string;
  owner_name?: string;
  created: number;
  start?: number;
  end?: number;
  team?: { _id: string }[];
  /** Prefixos distintos da equipe, contados no servidor. */
  prefixos?: number;
  indicator?: { nome?: string };
  participants_approval?: AprovacaoDeParticipantes;
  /** Fim da Apreciação Nível 3 do ciclo do cadastro, pela mesma regra do trigger (PAR-84). */
  prazo_para_desfazer?: number;
}

export interface Faixa {
  rotulo: string;
  alcada: string;
  /** Maior tamanho de equipe da faixa; `Infinity` na última. */
  ate: number;
}

/** Faixas de alçada, na ordem do design (PAR-02). Até 20 participantes a autorização é automática. */
export const FAIXAS: Faixa[] = [
  { rotulo: '21 a 30', alcada: '3º nível gerencial de UE ou 2º nível de UT, UA ou CA', ate: 30 },
  { rotulo: '31 a 50', alcada: '2º nível gerencial de UE ou 1º nível de UT, UA ou CA', ate: 50 },
  { rotulo: '51 a 100', alcada: 'Diretor, Auditor Geral ou Gerente Geral UE', ate: 100 },
  { rotulo: 'mais de 100', alcada: 'Vice-Presidente', ate: Infinity },
];

const SEM_INFORMACAO = '—';
const MESES_CURTOS = ['jan.', 'fev.', 'mar.', 'abr.', 'mai.', 'jun.', 'jul.', 'ago.', 'set.', 'out.', 'nov.', 'dez.'];

/** Tamanho da lista `team`; 0 sem lista (PAR-50). */
export function tamanhoDaEquipe(realizacao: RealizacaoParaAutorizar): number {
  return Array.isArray(realizacao.team) ? realizacao.team.length : 0;
}

/** Faixa pelo tamanho da equipe (PAR-06); null até 20, onde não há autorização a pedir. */
export function faixaDaEquipe(participantes: number): Faixa | null {
  if (participantes <= 20) return null;
  return FAIXAS.find(faixa => participantes <= faixa.ate) ?? null;
}

/** Quem vê a realização pode decidir por ela (PAR-07, PAR-08): mesma regra do fluxo de apreciação. */
export function dentroDaAlcada(playerInfo: unknown, realizacao: RealizacaoParaAutorizar): boolean {
  return canApproveParticipantsCount(playerInfo, realizacao);
}

export function statusDaAutorizacao(realizacao: RealizacaoParaAutorizar): StatusDaAutorizacao | null {
  const status = realizacao.participants_approval?.status;
  return status === 'pending' || status === 'approved' || status === 'rejected' ? status : null;
}

/**
 * "Desfazer" aparece com as mesmas guardas do trigger (PAR-37): decisão de quem está logado, status
 * ainda igual à decisão, equipe do mesmo tamanho e prazo da realização aberto (PAR-84). O servidor confere de novo.
 */
export function podeDesfazer(realizacao: RealizacaoParaAutorizar, usuario: string | undefined, agora: number): boolean {
  const aprovacao = realizacao.participants_approval;
  const prazo = realizacao.prazo_para_desfazer;
  return (
    !!usuario &&
    !!aprovacao?.decision &&
    aprovacao.decided_by === usuario &&
    aprovacao.status === aprovacao.decision &&
    aprovacao.decided_team_size === tamanhoDaEquipe(realizacao) &&
    typeof prazo === 'number' &&
    agora <= prazo
  );
}

/** Fim da Apreciação Nível 3 do ciclo vigente (`date_evaluation_step5_finish`); null sem calendário. */
export function prazoDoCiclo(cycles: CyclesInterface[] | undefined): number | null {
  const fins = etapasDoCiclo(cycles)
    .filter(etapa => etapa.tipo === 'n3')
    .map(etapa => etapa.fim);
  return fins.length ? Math.max(...fins) : null;
}

/** Início do ciclo vigente, para a leitura das realizações (PAR-03). */
export function inicioDoCiclo(cycles: CyclesInterface[] | undefined): number | null {
  const inicios = etapasDoCiclo(cycles).map(etapa => etapa.inicio);
  return inicios.length ? Math.min(...inicios) : null;
}

const doisDigitos = (valor: number) => String(valor).padStart(2, '0');

/** "dd/mm/aaaa" lido pelos getters UTC. */
function ddMmAaaa(data: Date): string {
  return `${doisDigitos(data.getUTCDate())}/${doisDigitos(data.getUTCMonth() + 1)}/${data.getUTCFullYear()}`;
}

/** "05/02/2027", no horário de Brasília. */
export function dataCurta(ms: number): string {
  return ddMmAaaa(emBrasilia(ms));
}

/** "12 ago. 26", no horário de Brasília (PAR-05). */
export function dataDoCadastro(ms: number): string {
  const data = emBrasilia(ms);
  return `${data.getUTCDate()} ${MESES_CURTOS[data.getUTCMonth()]} ${String(data.getUTCFullYear()).slice(-2)}`;
}

/** `start` e `end` são datas do calendário gravadas à meia-noite UTC: lidas em UTC, como no `TaskItem`. */
function dataDaEntrega(ms: number): string {
  return ddMmAaaa(new Date(ms));
}

/** "01/08/2026 a 18/08/2026" (PAR-09). */
export function periodoDaEntrega(realizacao: RealizacaoParaAutorizar): string {
  if (typeof realizacao.start !== 'number' || typeof realizacao.end !== 'number') return SEM_INFORMACAO;
  return `${dataDaEntrega(realizacao.start)} a ${dataDaEntrega(realizacao.end)}`;
}

/** Indicador da realização; "—" sem nome (PAR-51). */
export function indicadorDaRealizacao(realizacao: RealizacaoParaAutorizar): string {
  return realizacao.indicator?.nome || SEM_INFORMACAO;
}

const plural = (quantidade: number, singular: string) => `${quantidade} ${singular}${quantidade === 1 ? '' : 's'}`;

/** "{nome} ({chave}) e mais N participantes de K prefixos", K = `prefixos` do servidor, 0 sem ele (PAR-09, PAR-52). */
export function textoDaEquipe(realizacao: RealizacaoParaAutorizar): string {
  const cadastrante = `${realizacao.owner_name ?? ''} (${realizacao.owner ?? ''})`;
  const outros = Math.max(tamanhoDaEquipe(realizacao) - 1, 0);

  return `${cadastrante} e mais ${plural(outros, 'participante')} de ${plural(realizacao.prefixos ?? 0, 'prefixo')}`;
}
